import { create } from "zustand";
import { io, type Socket } from "socket.io-client";
import { settings, log } from "@tumbodash/browserPackages";
import { init as initGameEngine } from "@tumbodash/clientGameEngine";
import usePlayerStore from "./player.js";

interface SocketStore {
	socket: Socket | null;
	isConnected: boolean;
	connecting: boolean;
	init: () => Promise<Socket | null>;
	disconnectSocket: () => void;
}

const useSocketStore = create<SocketStore>((set, get) => ({
	socket: null,
	isConnected: false,
	connecting: false,

	init: () => {
		log.fx("(stores/socket:init)");

		const { socket, connecting } = get();
		if (socket) return Promise.resolve(socket);
		if (connecting) return Promise.resolve(null);

		const playerId = usePlayerStore.getState().getPlayerId();
		if (!playerId) throw new Error("Socket init called without playerId");

		set({ connecting: true });
		log.socket("Connecting to server at: %s", settings.serverUrl);

		return new Promise<Socket | null>((resolve, reject) => {
			const newSocket = io(settings.serverUrl!, {
				auth: { playerId },
				transports: ["websocket"],
			});

			newSocket.on("connect", () => {
				initGameEngine(newSocket);
				log.socket("✅ Socket Connected | PlayerID: %s | SocketID: %s", playerId, newSocket.id);
				set({ socket: newSocket, isConnected: true, connecting: false });
				resolve(newSocket);
			});

			newSocket.on("disconnect", (reason) => {
				log.socket("❌ Socket disconnected:", reason);
				set({ socket: null, isConnected: false, connecting: false });
			});

			newSocket.on("connect_error", (err) => {
				log.socket("❌ Socket connect error:", err.message);
				set({ connecting: false });
				reject(err);
			});

			newSocket.onAny((eventName: string) => {
				log.event("[socket:%s]", eventName);
			});
		});
	},

	disconnectSocket: () => {
		const socket = get().socket;
		if (!socket) return;
		log.socket("Disconnecting socket");
		socket.disconnect();
		set({ socket: null, isConnected: false, connecting: false });
	},
}));

export default useSocketStore;
